import React, { useMemo, useState } from 'react';
import { get, chain, take } from 'lodash';
import PropTypes from 'prop-types';
import { MessageBar, MessageBarType, Spinner, SpinnerSize, Text } from 'office-ui-fabric-react';
import useAPI from '../../hooks/useAPI';
import Module from '../Module';
import ModuleContent from '../ModuleContent';
import ModuleHeader from '../ModuleHeader';
import TextButton from '../TextButton';
import styles from './AdvanceGroupActivityList.style';
import { fetchAdvanceGroupActivity } from '../../actions/advanceGroups';
import AdvanceGroupActivityItems from '../AdvanceGroupActivityItems';

const AdvanceGroupActivityList = ({ advancegroupId }) => {
  const [count, setCount] = useState(3);

  if (!advancegroupId) return <Spinner size={SpinnerSize.medium} />;

  const { error, data, isLoading } = useAPI(
    () => fetchAdvanceGroupActivity(advancegroupId),
    [advancegroupId],
  );

  const rawActivities = get(data, 'entities.activity') || {};

  const activities = useMemo(() => {
    if (data) {
      let firstEdit = true;
      return chain(rawActivities)
        .values()
        .filter(
          activity =>
            activity.params.advancegroupId === advancegroupId ||
            activity.resource === advancegroupId,
        )
        .orderBy(['timestamp'], ['desc'])
        .filter(activity => {
          if (activity.operation === 'edited') {
            if (!firstEdit) return false;
            firstEdit = false;
          }
          return true;
        })
        .value();
    }
    return [];
  }, [rawActivities, advancegroupId]);

  return (
    <Module>
      <ModuleHeader
        extra={
          <Text aria-label="Expanded" role="alert" as="span" className={styles.activityShowing}>
            Showing{' '}
            {activities.length > (activities.length > count ? count - 3 : count) ? (
              <span>{count}</span>
            ) : (
              <span>{activities.length}</span>
            )}{' '}
            of <span>{activities.length}</span>
          </Text>
        }
        className={styles.advancegroupActivity}
      >
        Advance Group Activity
      </ModuleHeader>
      <ModuleContent className={styles.activityContent}>
        {error && (
          <MessageBar
            isMultiline={false}
            messageBarType={MessageBarType.error}
            dismissButtonAriaLabel="Close"
          >
            {error.name}: Try reloading or contact support.
          </MessageBar>
        )}
        {isLoading && (
          <>
            <br />
            <Spinner size={SpinnerSize.medium} />
          </>
        )}
        {activities && <AdvanceGroupActivityItems items={take(activities, count)} />}
        {activities && activities.length > 3 && activities.length > count && (
          <div>
            <TextButton
              tabIndex="0"
              noPadding
              aria-label="Show More Activity"
              className={styles.showMoreActivityButton}
              onClick={() => setCount(count + 10)}
            >
              <Text as="p" variant="small" className={styles.showMoreActivity}>
                Show More Activity
              </Text>
            </TextButton>
          </div>
        )}
      </ModuleContent>
    </Module>
  );
};

AdvanceGroupActivityList.propTypes = {
  advancegroupId: PropTypes.string.isRequired,
};

AdvanceGroupActivityList.defaultProps = {};

export default AdvanceGroupActivityList;
